import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ColumnChart from "../components/Charts/ColumnChart";
import PieChart from "../components/Charts/PieChart";
import StackedColumnChart from "../components/Charts/StackedColumnChart";
import LineChart from "../components/Charts/LineChart";
import { getManagers } from "../api/managerApi";
import { getPosts } from "../api/boardApi";
import { getDocs } from "../api/aprvDoc";
import { getStatByComCode, getStatHistory } from "../api/statApi";

function Portal() {
  const [managers, setManagers] = useState([]);
  const [userCountsByComCode, setUserCountsByComCode] = useState({});
  const [mailUserCountsByComCode, setMailUserCountsByComCode] = useState({});
  const [postCountsByComCode, setPostCountsByComCode] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedComCode, setSelectedComCode] = useState("");

  // 상세 조회용 상태
  const [selectedComUsers, setSelectedComUsers] = useState([]);
  const [selectedComAprvDocs, setSelectedComAprvDocs] = useState([]);
  const [selectedComPosts, setSelectedComPosts] = useState([]);
  const [selectedComStat, setSelectedComStat] = useState(null);
  const [statHistory, setStatHistory] = useState([]);
  const [detailPeriod, setDetailPeriod] = useState("daily");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [pmsHistory, setPmsHistory] = useState([]);
  const [safetyHistory, setSafetyHistory] = useState([]);
  const [pmsAmountPeriod, setPmsAmountPeriod] = useState("daily");
  const [safetyAprvPeriod, setSafetyAprvPeriod] = useState("daily");
  const [pmsPeriod, setPmsPeriod] = useState("daily");
  const [safetyPeriod, setSafetyPeriod] = useState("daily");

  useEffect(() => {
    let mounted = true;

    const loadManagers = async () => {
      try {
        setLoading(true);
        const list = await getManagers();
        if (!mounted) return;

        const managerList = Array.isArray(list) ? list : [];
        setManagers(managerList);

        const token = sessionStorage.getItem("token");
        const countEntries = await Promise.all(
          managerList.map(async (manager) => {
            const comCode = manager?.M_ComCode;
            if (!comCode) return ["", { userCount: 0, mailUserCount: 0, postCount: 0 }];

            try {
              const [usersRes, postsRes] = await Promise.all([
                axios.post(
                  "/api/auth/users",
                  { M_ComCode: comCode },
                  { headers: { Authorization: `Bearer ${token}` } }
                ),
                getPosts(1, 1, "", "", { token, M_ComCode: comCode }),
              ]);

              const users = Array.isArray(usersRes.data) ? usersRes.data : [];
              const userCount = users.length;
              const mailUserCount = users.filter((user) => user.maildb === 1 || user.maildb === true).length;
              const postCount = Number(postsRes?.total) || 0;
              return [comCode, { userCount, mailUserCount, postCount }];
            } catch (countErr) {
              console.error(`회사별 통계 조회 실패 (${comCode}):`, countErr);
              return [comCode, { userCount: 0, mailUserCount: 0, postCount: 0 }];
            }
          })
        );

        if (!mounted) return;
        const filteredEntries = countEntries.filter(([key]) => key);
        setUserCountsByComCode(
          Object.fromEntries(filteredEntries.map(([key, value]) => [key, value.userCount]))
        );
        setMailUserCountsByComCode(
          Object.fromEntries(filteredEntries.map(([key, value]) => [key, value.mailUserCount]))
        );
        setPostCountsByComCode(
          Object.fromEntries(filteredEntries.map(([key, value]) => [key, value.postCount]))
        );
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "포털 데이터를 불러오지 못했습니다.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadManagers();

    return () => {
      mounted = false;
    };
  }, []);

  // 선택된 회사의 상세 데이터 로드
  useEffect(() => {
    if (!selectedComCode) {
      setSelectedComUsers([]);
      setSelectedComAprvDocs([]);
      setSelectedComPosts([]);
      setSelectedComStat(null);
      setStatHistory([]);
      setDetailError("");
      return;
    }

    let mounted = true;

    const loadDetailData = async () => {
      try {
        setDetailLoading(true);
        setDetailError("");
        const token = sessionStorage.getItem("token");

        // 1. 사용자 목록 조회
        const usersRes = await axios.post(
          "/api/auth/users",
          { M_ComCode: selectedComCode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const userList = Array.isArray(usersRes.data) ? usersRes.data : [];
        if (mounted) setSelectedComUsers(userList);

        // 2. 전자결재 문서 목록 조회
        try {
          const docsRes = await getDocs(1, 999, "", "", token, selectedComCode);
          if (mounted) setSelectedComAprvDocs(Array.isArray(docsRes?.docs) ? docsRes.docs : []);
        } catch (err) {
          console.error("전자결재 문서 조회 실패:", err);
          if (mounted) setSelectedComAprvDocs([]);
        }

        // 3. 게시판 글 목록 조회
        try {
          const postsRes = await getPosts(1, 999, "", "", { token, M_ComCode: selectedComCode });
          if (mounted) setSelectedComPosts(Array.isArray(postsRes?.posts) ? postsRes.posts : []);
        } catch (err) {
          console.error("게시판 글 조회 실패:", err);
          if (mounted) setSelectedComPosts([]);
        }

        // 4. tdx_stat 최근 레코드 조회 (전자결재·게시판·일정·Todo 합산용)
        try {
          const statRes = await getStatByComCode(selectedComCode, token);
          if (mounted) setSelectedComStat(statRes?.stats?.[0] || null);
        } catch (err) {
          console.error("통계 조회 실패:", err);
          if (mounted) setSelectedComStat(null);
        }

        // 6. PMS 통계 이력 조회 (시계열)
        try {
          const pmsHistRes = await axios.get(`/api/stats/pms/history/${selectedComCode}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (mounted) setPmsHistory(pmsHistRes.data?.stats || []);
        } catch (err) {
          console.error("PMS 통계 이력 조회 실패:", err);
          if (mounted) setPmsHistory([]);
        }

        // 6. 일반 통계 이력 조회 (문서 추이용, 시계열)
        try {
          const statHistRes = await getStatHistory(selectedComCode, token);
          if (mounted) setStatHistory(statHistRes?.stats || []);
        } catch (err) {
          console.error("통계 이력 조회 실패:", err);
          if (mounted) setStatHistory([]);
        }

        // 7. Safety 통계 이력 조회 (시계열)
        try {
          const safetyHistRes = await axios.get(`/api/stats/safety/history/${selectedComCode}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (mounted) setSafetyHistory(safetyHistRes.data?.stats || []);
        } catch (err) {
          console.error("Safety 통계 이력 조회 실패:", err);
          if (mounted) setSafetyHistory([]);
        }
      } catch (err) {
        if (mounted) setDetailError(err?.response?.data?.message || "상세 데이터 조회 중 오류가 발생했습니다.");
      } finally {
        if (mounted) setDetailLoading(false);
      }
    };

    loadDetailData();

    return () => {
      mounted = false;
    };
  }, [selectedComCode]);

  const handleComCodeChange = (e) => {
    const comCode = e.target.value;
    setSelectedComCode(comCode);
  };

  const categories = useMemo(
    () => managers.map((manager) => manager.M_ComName || manager.M_ComCode || "회사"),
    [managers]
  );

  const columnSeries = useMemo(
    () => [
      {
        name: "사용자수",
        data: managers.map((manager) => userCountsByComCode[manager.M_ComCode] || 0),
      },
      {
        name: "메일사용자",
        data: managers.map((manager) => mailUserCountsByComCode[manager.M_ComCode] || 0),
      },
    ],
    [managers, userCountsByComCode, mailUserCountsByComCode]
  );

  const pieSeries = useMemo(
    () =>
      managers.map((manager) => ({
        name: manager.M_ComName || manager.M_ComCode || "회사",
        data: postCountsByComCode[manager.M_ComCode] || 0,
      })),
    [managers, postCountsByComCode]
  );

  const detailCategories = useMemo(
    () => selectedComUsers.map((user) => user.name || user.userid || "사용자"),
    [selectedComUsers]
  );

  const detailColumnSeries = useMemo(() => {
    const aprvData = selectedComUsers.map((user) => {
      return selectedComAprvDocs.filter((doc) => doc.user_id === user.userid).length;
    });

    const boardData = selectedComUsers.map((user) => {
      return selectedComPosts.filter((post) => post.writer_id === user.userid).length;
    });

    return [
      {
        name: "전자결재",
        data: aprvData,
      },
      {
        name: "게시판",
        data: boardData,
      },
    ];
  }, [selectedComUsers, selectedComAprvDocs, selectedComPosts]);

  const detailPieSeries = useMemo(() => {
    const s = selectedComStat;
    return [
      {
        name: "전자결재",
        data: (s?.stat_AprvDoneCnt || 0) + (s?.stat_AprvIngCnt || 0) +
              (s?.stat_AprvRejectCnt || 0) + (s?.stat_AprvDraftCnt || 0),
      },
      { name: "게시판", data: s?.stat_BbsCount || 0 },
      {
        name: "일정",
        data: (s?.stat_SchRegisterCnt || 0) + (s?.stat_SchRequestCnt || 0) +
              (s?.stat_SchRejectCnt || 0) + (s?.stat_SchDraftCnt || 0),
      },
      { name: "Todo", data: s?.stat_TodoCnt || 0 },
    ];
  }, [selectedComStat]);

  // 시계열 데이터를 period(daily/monthly/yearly)로 그룹핑하는 함수
  // 같은 기간 내 마지막 레코드(ASC 정렬이므로 마지막이 최신)를 대표값으로 사용
  const groupByPeriod = (rows, period) => {
    const map = {};
    rows.forEach((row) => {
      const d = new Date(row.created_at);
      let key;
      if (period === "yearly") key = String(d.getFullYear());
      else if (period === "monthly") key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      else key = d.toISOString().slice(0, 10);
      map[key] = row;
    });
    const keys = Object.keys(map).sort();
    return { keys, grouped: map };
  };

  const detailTrendData = useMemo(() => {
    const { keys, grouped } = groupByPeriod(statHistory, detailPeriod);
    return {
      categories: keys,
      series: [
        {
          name: "전자결재",
          data: keys.map((k) =>
            (grouped[k].stat_AprvDoneCnt || 0) + (grouped[k].stat_AprvIngCnt || 0) +
            (grouped[k].stat_AprvRejectCnt || 0) + (grouped[k].stat_AprvDraftCnt || 0)
          ),
        },
        { name: "게시판", data: keys.map((k) => grouped[k].stat_BbsCount || 0) },
        {
          name: "일정",
          data: keys.map((k) =>
            (grouped[k].stat_SchRegisterCnt || 0) + (grouped[k].stat_SchRequestCnt || 0) +
            (grouped[k].stat_SchRejectCnt || 0) + (grouped[k].stat_SchDraftCnt || 0)
          ),
        },
        { name: "Todo", data: keys.map((k) => grouped[k].stat_TodoCnt || 0) },
      ],
    };
  }, [statHistory, detailPeriod]);

  // 이력(ASC) 마지막 레코드 = 최근 날짜 스냅샷
  const pmsStat = useMemo(
    () => (pmsHistory.length > 0 ? pmsHistory[pmsHistory.length - 1] : null),
    [pmsHistory]
  );
  const safetyStat = useMemo(
    () => (safetyHistory.length > 0 ? safetyHistory[safetyHistory.length - 1] : null),
    [safetyHistory]
  );

  const pmsStackedCategories = ["지시", "프로젝트", "태스크"];

  const pmsStackedSeries = useMemo(() => {
    if (!pmsStat) return [];
    return [
      {
        name: "대기",
        data: [
          pmsStat.stat_InstructionPendingCnt || 0,
          pmsStat.stat_ProjectPendingCnt || 0,
          pmsStat.stat_TaskPendingCnt || 0,
        ],
      },
      {
        name: "진행",
        data: [
          pmsStat.stat_InstructionActiveCnt || 0,
          pmsStat.stat_ProjectActiveCnt || 0,
          pmsStat.stat_TaskActiveCnt || 0,
        ],
      },
      {
        name: "완료",
        data: [
          pmsStat.stat_InstructionCompletedCnt || 0,
          pmsStat.stat_ProjectCompletedCnt || 0,
          pmsStat.stat_TaskCompletedCnt || 0,
        ],
      },
    ];
  }, [pmsStat]);

  const pmsAmountTrendData = useMemo(() => {
    const { keys, grouped } = groupByPeriod(pmsHistory, pmsAmountPeriod);
    return {
      categories: keys,
      series: [
        { name: "매출", data: keys.map((k) => grouped[k].stat_BusinessOrderSalesAmt || 0) },
        { name: "외주비", data: keys.map((k) => grouped[k].stat_BusinessOrderPurchasesAmt || 0) },
        { name: "노무비", data: keys.map((k) => grouped[k].stat_BusinessOrderLaborAmt || 0) },
        { name: "경비", data: keys.map((k) => grouped[k].stat_BusinessOrderExpensesAmt || 0) },
      ],
    };
  }, [pmsHistory, pmsAmountPeriod]);

  const pmsTrendData = useMemo(() => {
    const { keys, grouped } = groupByPeriod(pmsHistory, pmsPeriod);
    return {
      categories: keys,
      series: [
        { name: "지시", data: keys.map((k) => grouped[k].stat_InstructionCnt || 0) },
        { name: "프로젝트", data: keys.map((k) => grouped[k].stat_ProjectCnt || 0) },
        { name: "태스크", data: keys.map((k) => grouped[k].stat_TaskCnt || 0) },
      ],
    };
  }, [pmsHistory, pmsPeriod]);

  const safetyTrendData = useMemo(() => {
    const { keys, grouped } = groupByPeriod(safetyHistory, safetyPeriod);
    return {
      categories: keys,
      series: [
        { name: "위험성평가", data: keys.map((k) => grouped[k].stat_AssessmentCnt || 0) },
        { name: "위험요인", data: keys.map((k) => grouped[k].stat_EvaluationCnt || 0) },
        { name: "교육", data: keys.map((k) => grouped[k].stat_TrainingCnt || 0) },
        { name: "회의", data: keys.map((k) => grouped[k].stat_MeetingCnt || 0) },
      ],
    };
  }, [safetyHistory, safetyPeriod]);

  const safetyStackedCategories = ["위험성평가", "위험요인"];

  const safetyStackedSeries = useMemo(() => {
    if (!safetyStat) return [];
    return [
      {
        name: "진행중",
        data: [
          safetyStat.stat_AssessmentInProgressCnt || 0,
          safetyStat.stat_EvalInProgressCnt || 0,
        ],
      },
      {
        name: "완료",
        data: [
          safetyStat.stat_AssessmentCompletedCnt || 0,
          safetyStat.stat_EvalCompletedCnt || 0,
        ],
      },
      {
        name: "취소/대기",
        data: [
          safetyStat.stat_AssessmentCanceledCnt || 0,
          safetyStat.stat_EvalPendingCnt || 0,
        ],
      },
    ];
  }, [safetyStat]);

  const safetyAprvTrendData = useMemo(() => {
    const { keys, grouped } = groupByPeriod(safetyHistory, safetyAprvPeriod);
    return {
      categories: keys,
      series: [
        { name: "기안", data: keys.map((k) => grouped[k].stat_AprvDocDraftCnt || 0) },
        { name: "진행중", data: keys.map((k) => grouped[k].stat_AprvDocProgressCnt || 0) },
        { name: "완료", data: keys.map((k) => grouped[k].stat_AprvDocCompleteCnt || 0) },
        { name: "반려", data: keys.map((k) => grouped[k].stat_AprvDocRejectCnt || 0) },
      ],
    };
  }, [safetyHistory, safetyAprvPeriod]);


  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "60vh" }}>
        <h6 className="text-muted mb-0">포털 데이터를 불러오는 중...</h6>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: "60vh" }}>
        <h6 className="text-danger mb-0">{error}</h6>
      </div>
    );
  }

  return (
    <div className="container-fluid py-2">
      <div className="row g-3">
        <div className="col-12 mb-3">
          <label htmlFor="comCodeSelect" className="form-label fw-bold">
            조회 범위:
          </label>
          <select
            id="comCodeSelect"
            className="form-select"
            value={selectedComCode}
            onChange={handleComCodeChange}
          >
            <option value="">전체</option>
            {managers.map((manager) => (
              <option key={manager.M_ComCode} value={manager.M_ComCode}>
                {manager.M_ComName}
              </option>
            ))}
          </select>
        </div>

        {!selectedComCode && (
          <>
            <div className="col-12 col-xl-6">
              <div className="card h-100">
                <div className="card-body">
                  <ColumnChart
                    categories={categories}
                    series={columnSeries}
                    title="회사별 사용자수"
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-6">
              <div className="card h-100">
                <div className="card-body">
                  <PieChart series={pieSeries} title="회사별 게시글 수" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedComCode && (
        <>
          <div className="row g-3 mt-4">
            <div className="col-12">
            <div className="card">
              <div className="card-header bg-light">
                <h5 className="mb-0">
                  {managers.find((m) => m.M_ComCode === selectedComCode)?.M_ComName || "선택된 회사"} - 사용자별 전자결재/게시판 활동
                </h5>
              </div>
              <div className="card-body">
                {detailLoading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">로딩 중...</span>
                    </div>
                  </div>
                ) : detailError ? (
                  <div className="alert alert-danger mb-0">{detailError}</div>
                ) : selectedComUsers.length === 0 ? (
                  <div className="alert alert-info mb-0">선택된 회사의 사용자가 없습니다.</div>
                ) : (
                  <ColumnChart
                    categories={detailCategories}
                    series={detailColumnSeries}
                    title="사용자별 활동"
                  />
                )}
              </div>
            </div>
          </div>
          </div>

          {!detailLoading && !detailError && (
            <div className="row g-3 mt-1">
              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-body">
                    <PieChart series={detailPieSeries} title="문서 유형별 건수" />
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">문서 유형별 추이</h6>
                    <div className="btn-group btn-group-sm" role="group">
                      {[
                        { key: "daily", label: "일별" },
                        { key: "monthly", label: "월별" },
                        { key: "yearly", label: "연도별" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${detailPeriod === key ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setDetailPeriod(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {detailTrendData.categories.length > 0 ? (
                      <LineChart
                        categories={detailTrendData.categories}
                        series={detailTrendData.series}
                        title=""
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 320, color: "#94a3b8" }}>
                        <span>이력 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!detailLoading && !detailError && (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">PMS 통계</h5>
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-body">
                    {pmsStat ? (
                      <StackedColumnChart
                        categories={pmsStackedCategories}
                        series={pmsStackedSeries}
                        title="지시 / 프로젝트 / 태스크 상태별 현황"
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 305, color: "#94a3b8" }}>
                        <span>PMS 통계 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">수주금액 추이 (매출 / 외주비 / 노무비 / 경비)</h6>
                    <div className="btn-group btn-group-sm" role="group">
                      {[{ key: "daily", label: "일별" }, { key: "monthly", label: "월별" }, { key: "yearly", label: "연도별" }].map(({ key, label }) => (
                        <button key={key} type="button" className={`btn ${pmsAmountPeriod === key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setPmsAmountPeriod(key)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {pmsAmountTrendData.categories.length > 0 ? (
                      <LineChart
                        categories={pmsAmountTrendData.categories}
                        series={pmsAmountTrendData.series}
                        title=""
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 305, color: "#94a3b8" }}>
                        <span>수주금액 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!detailLoading && !detailError && (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">위험성평가 통계</h5>
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-body">
                    {safetyStat ? (
                      <StackedColumnChart
                        categories={safetyStackedCategories}
                        series={safetyStackedSeries}
                        title="위험성평가 / 위험요인 상태별 현황"
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 305, color: "#94a3b8" }}>
                        <span>위험성평가 통계 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">결재서류 추이 (기안 / 진행중 / 완료 / 반려)</h6>
                    <div className="btn-group btn-group-sm" role="group">
                      {[{ key: "daily", label: "일별" }, { key: "monthly", label: "월별" }, { key: "yearly", label: "연도별" }].map(({ key, label }) => (
                        <button key={key} type="button" className={`btn ${safetyAprvPeriod === key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setSafetyAprvPeriod(key)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {safetyAprvTrendData.categories.length > 0 ? (
                      <LineChart
                        categories={safetyAprvTrendData.categories}
                        series={safetyAprvTrendData.series}
                        title=""
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 305, color: "#94a3b8" }}>
                        <span>결재서류 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {!detailLoading && !detailError && (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <div className="card">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">추이 분석</h5>
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">PMS 추이 (지시 / 프로젝트 / 태스크)</h6>
                    <div className="btn-group btn-group-sm" role="group">
                      {[{ key: "daily", label: "일별" }, { key: "monthly", label: "월별" }, { key: "yearly", label: "연도별" }].map(({ key, label }) => (
                        <button key={key} type="button" className={`btn ${pmsPeriod === key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setPmsPeriod(key)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {pmsTrendData.categories.length > 0 ? (
                      <LineChart
                        categories={pmsTrendData.categories}
                        series={pmsTrendData.series}
                        title=""
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 320, color: "#94a3b8" }}>
                        <span>PMS 이력 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-6">
                <div className="card h-100">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">위험성평가 추이 (평가 / 위험요인 / 교육 / 회의)</h6>
                    <div className="btn-group btn-group-sm" role="group">
                      {[{ key: "daily", label: "일별" }, { key: "monthly", label: "월별" }, { key: "yearly", label: "연도별" }].map(({ key, label }) => (
                        <button key={key} type="button" className={`btn ${safetyPeriod === key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setSafetyPeriod(key)}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="card-body">
                    {safetyTrendData.categories.length > 0 ? (
                      <LineChart
                        categories={safetyTrendData.categories}
                        series={safetyTrendData.series}
                        title=""
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: 320, color: "#94a3b8" }}>
                        <span>위험성평가 이력 데이터가 없습니다.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Portal;